import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { ArrowDownIcon } from '@heroicons/react/solid'
import Parser from 'rss-parser'

interface Article {
  image: string
  href: string
  title: string
  author?: string
  date: string
}

interface NewsroomProps {
  articles: Article[]
  news: Article[]
}

const majordomoNews: Article[] = [
  {
    image: '/images/newsroom/inman-3.jpg',
    href:
      'https://www.inman.com/2020/09/01/majordomo-releases-app-for-pre-inspection-home-ratings/',
    title: 'Majordomo releases app for pre-inspection home ratings',
    author: 'By Craig Rowe, Inman',
    date: 'September 1, 2020'
  },
  {
    image: '/images/newsroom/ris-media.jpg',
    href:
      'https://rismedia.com/2020/01/16/disruptor-roundup-majordomo-home-inspections/',
    title:
      'Disruptor Roundup: Majordomo Looks to Deliver Clarity to Home Inspections',
    author: 'by Liz Dominguez, RISMedia',
    date: 'January 17, 2020'
  },
  {
    image: '/images/newsroom/inman-1.jpg',
    href:
      'https://www.inman.com/2019/12/11/10-tech-products-that-impressed-us-this-year/',
    title: '10 tech products we loved in 2019',
    author: 'by Craig Rowe, Inman',
    date: 'December 11, 2019'
  },
  {
    image: '/images/newsroom/inman-2.jpg',
    href:
      'https://www.inman.com/2019/11/26/reduce-escrow-friction-with-majordomos-post-inspection-platform/',
    title: 'Reduce escrow friction with Majordomo',
    author: 'by Craig Rowe, Inman',
    date: 'November 26, 2019'
  },
  {
    image: '/images/newsroom/gazette-times.jpg',
    href:
      'https://www.gazettetimes.com/business/local/gizmos-gadgets-and-great-ideas-at-innovation-expo/article_2d2750b7-e969-57dd-ab6a-5bf31d25d7ef.html',
    title: 'Gizmos, gadgets and great ideas at innovation expo',
    author: 'by Jim Day, Corvallis Gazette-Times',
    date: 'November 15, 2018'
  }
]

export async function getStaticProps() {
  const parser = new Parser()
  const articles: Article[] = []
  const { items } = await parser.parseURL(
    `${process.env.NEXT_PUBLIC_BLOG_URL}/tag/newsroom/feed/`
  )

  if (items) {
    items.forEach((item, index) => {
      const regex = /src\s*=\s*"(.+?)"/g
      const image = regex.exec(item.content ?? '')?.[1]
      const date = new Date(item.isoDate ?? '')
      const month = date.toLocaleString('default', {
        month: 'long'
      })
      const formattedDate = `${month} ${date.getDate()}, ${date.getFullYear()}`

      if (index < 3) {
        articles.push({
          image: image ?? '',
          title: item.title ?? '',
          href: item.link ?? '',
          date: formattedDate
        })
      }
    })
  }

  return {
    props: {
      articles,
      news: majordomoNews
    }
  }
}

export default function Newsroom(props: NewsroomProps) {
  return (
    <>
      <NextSeo
        title="Newsroom | Helpful Insights on Home Inspections & More"
        description="Stay up-to-date with our latest company news regarding home inspections, repair estimates & more. Read more here."
        canonical="https://majordomo.com/majordomos-newsroom"
        openGraph={{
          url: 'https://majordomo.com/majordomos-newsroom',
          title:
            'Newsroom | Helpful Insights on Home Inspections & More | Majordomo',
          description:
            'Stay up-to-date with our latest company news regarding home inspections, repair estimates & more. Read more here.',
          images: [
            {
              url: 'https://majordomo.com/images/newsroom/newsroom.png',
              width: 771,
              height: 691,
              alt: 'Person reading a newspaper while sitting on a bench'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex flex-col items-center justify-between pb-12 md:flex-row lg:pb-36">
          <div className="flex flex-col items-center justify-center order-last w-full md:items-start 2xl:p-36 xl:p-20 md:p-8 md:order-first">
            <div className="flex items-center pb-4 mb-4 border-gray-200 md:w-full">
              <h1 className="text-2xl font-black tracking-wide">
                Majordomo Newsroom
              </h1>
            </div>

            <p className="w-full max-w-lg px-8 md:p-0">
              Stay up to date with Majordomo company news and media coverage.
              You can also see a dynamic sample Domoreport{' '}
              <a
                href="/demo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accentBlue hover:underline"
              >
                here
              </a>
              .
            </p>

            <p className="flex items-center mt-12 font-black">
              Check out news and media below{' '}
              <ArrowDownIcon
                width={24}
                height={24}
                className="ml-2 text-gold-400"
              />
            </p>
          </div>

          <img
            src="/images/newsroom/newsroom.png"
            className="order-first mb-8 md:order-last md:mb-0"
            width="50%"
          />
        </div>

        <div className="px-8 mx-auto max-w-7xl">
          <div className="pb-12 lg:pb-36">
            <h4 className="mb-12 text-2xl font-black border-b border-black">
              Company News and Announcements
            </h4>

            <div className="relative flex items-center justify-start mx-auto">
              <div className="justify-start w-full grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
                {props.articles.map(post => (
                  <div
                    className="bg-white rounded-lg shadow-md"
                    key={post.href}
                  >
                    <img
                      src={post.image}
                      className="object-cover w-full rounded-t-lg h-52 max-h-52"
                    />
                    <div className="p-4">
                      <a
                        href={post.href}
                        className="font-black text-accentBlue hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <p className="mb-2 leading-tight">{post.title}</p>
                      </a>
                      <p className="text-sm text-gray-500">{post.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pb-12 lg:pb-36">
            <h4 className="mb-12 text-2xl font-black border-b border-black">
              In The News
            </h4>

            <div className="grid md:grid-rows-3 lg:grid-cols-2 grid-cols-1 gap-4">
              {props.news.map(article => (
                <div
                  className="flex flex-col max-w-4xl border border-gray-100 rounded-lg shadow-md md:flex-row bg-gray-50"
                  key={article.href}
                >
                  <div className="w-full md:w-1/2">
                    <img
                      src={article.image}
                      className="object-cover w-full h-full md:rounded-l-lg max-h-52"
                    />
                  </div>
                  <div className="relative flex flex-col justify-center w-full p-8 md:w-1/2">
                    <a
                      href={article.href}
                      className="mb-2 font-black text-accentBlue hover:underline lg:text-lg lg:leading-tight lg:mb-4 text-md"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {article.title}
                    </a>
                    <p>{article.author}</p>
                    <p className="mt-2 text-sm text-gray-500">{article.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col pb-12 md:flex-row lg:pb-36 md:space-x-8 md:space-y-0 space-y-8">
            <div className="w-full md:w-1/2 spacy-y-4">
              <h4 className="mb-12 text-2xl font-black border-b border-black">
                Press Inquiries
              </h4>
              <p className="mb-8">
                Majordomo loves working with journalists to share inspiring,
                creative, and compelling stories. If you’re a member of the
                media and would like to get in touch, please send us a note.
              </p>
              <a
                href="mailto:hello@majordomo.com"
                className="text-accentBlue hover:underline"
              >
                hello@majordomo.com
              </a>
            </div>

            <div className="w-full md:w-1/2 spacy-y-4">
              <h4 className="mb-12 text-2xl font-black border-b border-black">
                Speaking Inquiries
              </h4>
              <p className="mb-8">
                If you’re interested in having someone from Majordomo speak at
                your event, please send us an email with detailed event and
                topic information.
              </p>
              <a
                href="mailto:hello@majordomo.com"
                className="text-accentBlue hover:underline"
              >
                hello@majordomo.com
              </a>
            </div>
          </div>

          <div className="flex pb-12 lg:pb-36 space-x-8">
            <div className="w-full">
              <h4 className="mb-12 text-2xl font-black border-b border-black">
                Brand Assets
              </h4>
              <h4 className="mb-2 text-lg font-black">Our Name</h4>

              <p className="mb-8">
                “Majordomo” is one word, spelled with a big M and a little d.
              </p>

              <h4 className="mb-2 text-lg font-black">Our Logo</h4>

              <p className="mb-8">
                You can use the icon alone or with the name. Right click and
                save as.
              </p>

              <h4 className="mb-2 text-lg font-black">Our Colors</h4>

              <p className="mb-8">
                Dark Cerulean and Nickel are Majordomo’s logo colors. We use
                Amber for accents.
              </p>

              <div className="flex items-center justify-center space-x-8">
                <div className="flex items-center justify-center text-white rounded-full lg:h-60 md:h-48 h-28 lg:w-60 md:w-48 w-28 bg-brand">
                  #0A3782
                </div>
                <div className="flex items-center justify-center text-white bg-gray-500 rounded-full lg:h-60 md:h-48 h-28 lg:w-60 md:w-48 w-28">
                  #717171
                </div>
                <div className="flex items-center justify-center text-black rounded-full lg:h-60 md:h-48 h-28 lg:w-60 md:w-48 w-28 bg-gold-400">
                  #FFBF00
                </div>
              </div>
            </div>
          </div>

          <div className="flex pb-12 lg:pb-36 space-x-8">
            <div className="w-full">
              <h4 className="mb-12 text-2xl font-black border-b border-black">
                Terms and Definitions
              </h4>

              <div className="grid grid-cols-3 gap-8">
                <div>
                  <h4 className="mb-2 text-lg font-black">Majordomo</h4>

                  <p className="mb-8">
                    According to the Merriam-Webster dictionary, a{' '}
                    <span className="italic">majordomo</span> (noun) is “a head
                    steward of a large household (such as a palace).” You can
                    also think of it as the head butler of a house.{' '}
                    <Link href="/blog/majordomo">
                      <a className="text-accentBlue hover:underline">
                        Read more
                      </a>
                    </Link>
                  </p>
                </div>

                <div>
                  <h4 className="mb-2 text-lg font-black">Domoreport</h4>

                  <p className="mb-8">
                    This is our report of ZIP Code specific repair estimates
                    based on your home inspection. We break down the repair
                    estimates by materials & labor, and prioritizes them by
                    severity & urgency. After reviewing, you can create a
                    request list of repairs and credits to use in final
                    negotiations.{' '}
                    <Link href="/features/domoreport-repair-estimates">
                      <a className="text-accentBlue hover:underline">
                        Learn More
                      </a>
                    </Link>
                  </p>
                </div>

                <div>
                  <h4 className="mb-2 text-lg font-black">
                    The Second Half of the Home Inspection
                  </h4>

                  <p className="mb-8">
                    The period of time between when you receive the inspection
                    report and finish negotiations on any repairs or credits.
                    This includes prioritizing repairs, getting contractor
                    quotes, and writing a repair addendum.{' '}
                    <a
                      href="https://intercom.help/majordomo/en/articles/5412854-what-is-the-second-half-of-the-home-inspection"
                      className="text-accentBlue hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Read more
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex pb-12 lg:pb-36 space-x-8">
            <div className="w-full">
              <h4 className="mb-12 text-2xl font-black border-b border-black">
                Agent Toolkit
              </h4>
              <p>
                Are you an agent looking to download free resources from
                Majordomo to explain to your buyers how Majordomo fits into your
                buying process?{' '}
                <Link href="/home-agent-toolkit">
                  <a className="text-accentBlue hover:underline">
                    Check them out
                  </a>
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
